import { expandJson } from '@chiroup/core/functions/jsonMinify';
import {
  AdHocColumnDef,
  AdHocReportDescriptor,
  AdHocReportListResponse,
  AdHocReportRun,
  AdHocReportRunResponse,
} from '@chiroup/core/types/AdHocReport.type';
import { use, useCallback, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { ToastContext, ToastTypes } from '../../contexts/toast.context';
import adHocReportService from '../../services/adHocReport.service';

const useAdHocReport = (descriptor: AdHocReportDescriptor, clinicId: number | null) => {
  const { createToast } = use(ToastContext);
  const queryClient = useQueryClient();

  const [resultData, setResultData] = useState<any[] | null>(null);
  const [storedColumns, setStoredColumns] = useState<AdHocColumnDef[] | null>(null);
  const [activeRun, setActiveRun] = useState<AdHocReportRun | null>(null);
  const [fetchingRunId, setFetchingRunId] = useState<string | null>(null);
  const [deletingRunIds, setDeletingRunIds] = useState<Set<string>>(new Set());

  const listRunsQuery = useQuery<AdHocReportListResponse>(
    ['adHocReportRuns', clinicId],
    () => adHocReportService.listAdHocReportRuns({ clinicId: clinicId! }),
    {
      enabled: !!clinicId,
      refetchOnWindowFocus: false,
    }
  );

  const runs = useMemo(
    () => (listRunsQuery.data?.runs ?? []).filter(run => run.reportId === descriptor.id),
    [descriptor.id, listRunsQuery.data?.runs]
  );

  const runMutation = useMutation({
    mutationFn: (args: {
      name: string;
      params: Record<string, unknown>;
      columns: AdHocColumnDef[];
    }) =>
      adHocReportService.runAdHocReport({
        clinicId: clinicId!,
        body: {
          reportId: descriptor.id,
          name: args.name,
          params: args.params,
          columns: args.columns,
        },
      }),
    onSuccess: (response: AdHocReportRunResponse) => {
      setActiveRun(response.run);
      setStoredColumns(response.columns ?? null);
      queryClient.invalidateQueries(['adHocReportRuns', clinicId]);

      if (response.data) {
        setResultData(expandJson(response.data));
      }
    },
    onError: (err: any) => {
      queryClient.invalidateQueries(['adHocReportRuns', clinicId]);
      createToast({
        type: ToastTypes.Fail,
        title: 'Report Failed',
        duration: 5000,
        description:
          err?.response?.data?.message || err?.message || 'An error occurred running the report.',
      });
    },
  });

  const loadRunResults = async (run: AdHocReportRun) => {
    if (!clinicId) return;
    setResultData(null);
    setStoredColumns(null);
    setActiveRun(run);
    setFetchingRunId(run.id);

    try {
      const response: AdHocReportRunResponse = await adHocReportService.getAdHocReportRun({
        clinicId,
        runId: run.id,
      });

      setStoredColumns(response.columns ?? null);

      if (response.data) {
        setResultData(expandJson(response.data));
      }
    } catch (err: any) {
      setActiveRun(null);
      setStoredColumns(null);
      createToast({
        type: ToastTypes.Fail,
        title: 'Load Failed',
        duration: 5000,
        description:
          err?.response?.data?.message ||
          err?.message ||
          'An error occurred loading the report results.',
      });
    } finally {
      setFetchingRunId(null);
    }
  };

  const deleteRun = async (runId: string) => {
    if (!clinicId) return;
    setDeletingRunIds(prev => new Set(prev).add(runId));

    try {
      await adHocReportService.deleteAdHocReportRun({ clinicId, runId });
      queryClient.invalidateQueries(['adHocReportRuns', clinicId]);

      if (activeRun?.id === runId) {
        setResultData(null);
        setActiveRun(null);
      }
    } catch (err: any) {
      createToast({
        type: ToastTypes.Fail,
        title: 'Delete Failed',
        duration: 5000,
        description:
          err?.response?.data?.message ||
          err?.message ||
          'An error occurred deleting the report run.',
      });
    } finally {
      setDeletingRunIds(prev => {
        const next = new Set(prev);
        next.delete(runId);

        return next;
      });
    }
  };

  const clearResults = useCallback(() => {
    setResultData(null);
    setStoredColumns(null);
    setActiveRun(null);
  }, []);

  return {
    runs,
    isLoadingRuns: listRunsQuery.isFetching,
    runReport: runMutation.mutateAsync,
    isRunning: runMutation.isLoading,
    resultData,
    storedColumns,
    activeRun,
    loadRunResults,
    fetchingRunId,
    deleteRun,
    deletingRunIds,
    clearResults,
  };
};

export default useAdHocReport;
